<template>
  <div class="apple-pay-widget">
    <apple-pay-button
      v-if="isSupported"
      buttonstyle="black"
      type="buy"
      locale="en-GB"
      @click="onApplePayClicked"
    />
  </div>
</template>
<script>
import Vue from 'vue'
import { trackError } from '@calculatorScripts/telemetry.js'
import { loadApplePaySDK } from '@vue/utils/devicePayLoader'
import httpCommerce from '@vue/services/api/httpCommerce'
import { getToken } from '@vue/services/jwt.service'

Vue.config.ignoredElements = ['apple-pay-button']

export default {
  name: 'ApplePayWidget',

  props: {
    // The total amount to charge, e.g. '9.99'
    amount: {
      type: String,
      required: true,
    },
    // ISO 4217 currency code, e.g. 'GBP'
    currencyCode: {
      type: String,
      default: 'GBP',
    },
    // ISO 3166-1 alpha-2 country code of your merchant
    countryCode: {
      type: String,
      default: 'GB',
    },
    // Your merchant identifier registered with Apple
    merchantIdentifier: {
      type: String,
      required: true,
    },
    // Label shown on the Apple Pay payment sheet
    merchantName: {
      type: String,
      required: true,
    },
    acceptCreditCards: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      isSupported: false,
      session: null
    };
  },

  async mounted() {
    try {
      await loadApplePaySDK();
      this.isSupported = this.checkApplePaySupport();
    } catch (err) {
      this.isSupported = false;
      trackError(err);
    } finally {
      this.$emit('available', this.isSupported);
    }
  },

  beforeDestroy() {
    this.abortSession();
  },

  methods: {
    checkApplePaySupport() {
      return (
        window.ApplePaySession &&
        ApplePaySession.supportsVersion(3) &&
        ApplePaySession.canMakePayments()
      );
    },

    waitForOrderConfirmation() {
      return new Promise((resolve, reject) => {
        this.$emit('confirmOrder', {
          ...null,
          resolve,
          reject
        });
      });
    },

    onApplePayClicked() {
      this.$emit('click');

      const request = this.buildPaymentRequest();

      // ApplePaySession must be created directly inside a user gesture handler
      try {
        this.session = new ApplePaySession(3, request);
      } catch (err) {
        console.error('Failed to create ApplePaySession:', err);
        return;
      }
      this.session.onvalidatemerchant = this.onValidateMerchant;
      this.session.onpaymentauthorized = this.onPaymentAuthorised;
      this.session.oncancel = this.onCancel;

      this.session.begin();
    },

    getMerchantCapabilities() {
      let arr = ['supports3DS', 'supportsDebit'];
      if (this.acceptCreditCards) {
        arr.push('supportsCredit');
      }

      return arr;
    },

    buildPaymentRequest() {
      return {
        countryCode: this.countryCode,
        currencyCode: this.currencyCode,
        merchantCapabilities: this.getMerchantCapabilities(),
        supportedNetworks: ['visa', 'masterCard', 'maestro'],
        total: {
          label: this.merchantName,
          type: 'final',
          amount: this.amount
        }
      };
    },

    /**
     * Apple calls this immediately after session.begin()
     * This makes a call to ecom to validate the merchant identity and
     * return a valid merchant session object (encrypted)
     */
    async onValidateMerchant(event) {
      try {
        await this.waitForOrderConfirmation();

        const merchantSession = await this.validateMerchantWithServer(
          event.validationURL
        );
        this.session.completeMerchantValidation(merchantSession);
      } catch (err) {
        this.session.abort();
        this.$emit('error', { stage: 'merchantValidation', error: err });
      }
    },

    /**
     * Called as the user authorises the payment with Biometrics,
     * we send the payment token to ecom for handling by SmartPay
     */
    async onPaymentAuthorised(event) {
      if (!this.session) {
        return;
      }

      try {
        const {token } = event.payment;
        const data = await this.processPaymentOnServer(token);

        this.session.completePayment(ApplePaySession.STATUS_SUCCESS);
        if (data.redirectUrl) {
          this.$emit('3ds', { url: data.redirectUrl });
        } else {
          this.$emit('success', data);
        }
      } catch (err) {
        if (this.session) {
          this.session.completePayment(ApplePaySession.STATUS_FAILURE);
        }
        this.$emit('error', { stage: 'paymentAuthorized', error: err });
      }
    },

    /**
     * Called if the user dismissed the payment sheet,
     * or if the ApplePay session times out
     */
    onCancel() {
      this.session = null;
      this.$emit('cancel');
    },

    // **** ecom calls ****

    /**
     * Verifies the merchant calls are going through our system
     * and returns an encrypted session object for use by Apple
     * @param validationURL
     * @returns {Promise<*>}
     */
    async validateMerchantWithServer(validationURL) {
      const bearerToken = await getToken();
      const { data } = await httpCommerce.post(
        'device-payment/validate-merchant',
        {
          validationURL,
          merchantIdentifier: this.merchantIdentifier
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`
          }
        }
      );

      return data
    },

    /**
     * Sends the payment token off to ecom to be handled by SmartPay
     * @param token
     * @return {Promise<*>}
     */
    async processPaymentOnServer(token) {
      const bearerToken = await getToken();
      const response = await httpCommerce.post(
        'device-payment/process-apple-payment',
        {
          token,
          amount: this.amount,
          currencyCode: this.currencyCode
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${bearerToken}`
          }
        }
      );

      return {
        ...response.data,
        redirectUrl: response.headers['location'] ?? null
      };
    },

    /**
     * Aborts the session to prevent re-use attack
     */
    abortSession() {
      if (this.session) {
        try {
          this.session.abort();
        } catch (_){
          // ignored
        } finally {
          this.session = null;
        }
      }
    }
  }
};
</script>
